import React from 'react';
import clsx from 'clsx';

interface MetricTileCardProps {
  children?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  header?: React.ReactNode;
  /** Tighter title→content spacing for the chart-style tile layout. */
  compact?: boolean;
}

/**
 * MetricTileCard is a specialized card component for MetricTile.
 * Unlike the shared Card component (which uses <button>), this uses a <div>
 * to allow nested interactive elements (like edit/unlink buttons).
 */
export default function MetricTileCard({
  children,
  onClick,
  className = '',
  header,
  compact = false
}: MetricTileCardProps) {
  const isClickable = typeof onClick === 'function';

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              // Nested header buttons (Comments, Edit, See details, …) also
              // receive Enter/Space. Only activate drilldown when the card
              // wrapper itself is focused, otherwise the inner control's
              // default action is stolen and the group opens as well.
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={clsx(
        'rounded-2xl flex flex-col transition-all relative',
        'w-full min-w-0 min-h-0 overflow-hidden',
        'bg-white border-[2px] border-border-default',
        'text-left items-start',
        compact ? 'px-6 py-3' : 'px-6 py-4',
        {
          'cursor-pointer hover:bg-blue-200/20': isClickable
        },
        className
      )}
    >
      {/* Header slot */}
      {header && <div className={clsx('w-full min-w-0 shrink-0', compact ? undefined : 'mb-2')}>{header}</div>}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col w-full min-h-0 min-w-0 overflow-hidden">
        {children && (
          <div
            className={clsx(
              'flex-1 min-h-0 min-w-0',
              compact
                ? 'overflow-hidden mt-1'
                : 'mt-2 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:hidden'
            )}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
